import Cookies from 'js-cookie'
import KP5CONSTANTS from './constants'
import getConfig from 'next/config'

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig()

function translateTypeToInternalType(type) {
  const mapping = {
    others: 'others',
    'ee-devices': 'enterpriseEdition',
    'se-devices': 'specialEdition'
  }
  return mapping[type]
}

/* Server Side APIS */
export const getLicenses = async (cookie, type, incomingCustomerId) => {
  try {
    const internalType = translateTypeToInternalType(type)
    const query = new URLSearchParams()
    query.append('cacheInvalidate', 'true')
    if (internalType) {
      query.append('licenseType', internalType)
      if (incomingCustomerId) {
        query.append('incomingCustomerId', incomingCustomerId)
      }
    }
    const apiUrl = `${serverRuntimeConfig.licenseApiBaseUrl}/api/v1/licenses?${query.toString()}`
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: cookie && { cookie }
    })
    const responseJSON = await response.json()
    return responseJSON
  } catch (err) {
    console.log(err)
    return Error(err)
  }
}

/* Client Side APIS */
export const getLicensesUI = async (filters = [], ...args) => {
  try {
    const [type, currentPage, pageSize] = args
    const internalType = translateTypeToInternalType(type)
    const query = new URLSearchParams()
    query.append('cacheInvalidate', 'true')
    if (internalType) {
      query.append('page', currentPage)
      query.append('limit', pageSize)
      query.append('licenseType', internalType)
      const incomingCustomerId = Cookies.get(KP5CONSTANTS.MANAGED_TENANT_KEY)
      if (incomingCustomerId) {
        query.append('incomingCustomerId', incomingCustomerId)
      }
    }
    if (filters?.length) {
      filters.forEach((filter) => query.append('licenseFilter', filter))
    }

    const apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/licenses?${query.toString()}`
    const response = await fetch(apiUrl)
    const responseJSON = await response.json()
    return responseJSON
  } catch (err) {
    console.log(err)
    return Error(err)
  }
}

export const getLicenseKeyDetails = async (licenseKey) => {
  try {
    let apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/license/details?licenseKey=${licenseKey}`
    const response = await fetch(apiUrl)
    if (!response.ok) {
      return { requestID: response.headers.get('x-request-id') }
    }
    const responseJSON = await response.json()

    return responseJSON
  } catch (err) {
    console.log(err)
    return Error(err)
  }
}

export const postDeviceStatus = async (licenseKey, devices, file) => {
  try {
    let response = null
    const data = {
      licenseKey
    }
    let apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/device/status`
    if (file) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('licenseKey', licenseKey)
      formData.append('response', 'csv')
      response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        headers: new Headers()
      })
    } else {
      if (devices?.length) {
        data.devices = devices.split(',').map((item) => item.trim())
      }
      data.response = 'csv'
      response = await fetch(apiUrl, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }
    return response
  } catch (err) {
    console.log(err)
    return Error(err)
  }
}

export const deactivateDevices = async (licenseKey, type, devices, isKeep, file) => {
  try {
    let response = null
    const data = {
      licenseKey
    }
    const action = isKeep ? 'keep' : 'remove'
    let apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/device/`
    apiUrl += type === 'default' ? 'deactivate' : 'deactivateBlackberry' // TODO: update this when route changes
    if (file) {
      const formData = new FormData()
      formData.append('file', file)
      if (licenseKey) {
        formData.append('licenseKey', licenseKey)
      }
      formData.append('response', 'csv')
      //if (type === 'default') {
      formData.append('action', action)
      //}
      response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        headers: new Headers()
      })
    } else {
      if (devices?.length) {
        data.devices = devices.split(',').map((item) => item.trim())
      }
      if (type === 'default') {
        data.action = action
      }
      data.response = 'csv'
      response = await fetch(apiUrl, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json'
        }
      })
    }
    return response
  } catch (err) {
    console.log(err)
    return Error(err)
  }
}

export const upgradeLicense = async (licenseKey) => {
  try {
    const apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/license/upgrade`
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ licenseKey })
    }
    const response = await fetch(apiUrl, requestOptions)
    if (!response.ok) {
      return { requestID: response.headers.get('x-request-id') }
    }
    const responseJSON = await response.json()

    return responseJSON
  } catch (err) {
    console.error(err)
    return Error(err)
  }
}

export const kmeGenerateLicense = async (activationKey) => {
  try {
    const apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/kme/license`
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ activationKey })
    }
    const response = await fetch(apiUrl, requestOptions)
    const responseJSON = await response.json()
    return responseJSON
  } catch (err) {
    console.error(err)
    return Error(err)
  }
}

export const knoxEfotaRequestFiles = async (data, type = 'license') => {
  const { customerLicense, macAddress, scenarioJson } = data
  try {
    const apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/efota/onPrem/${
      type === 'license' ? 'requestLicenseFile' : 'requestFirmware'
    }`
    const otherData = type === 'license' ? { macAddress } : { scenarioJson }
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ customerLicense, ...otherData })
    }
    const response = await fetch(apiUrl, requestOptions)
    const responseJSON = await response.json()
    return responseJSON
  } catch (err) {
    console.error(err)
    return Error(err)
  }
}

export const getKnoxFirmwareRequests = async (customerLicense) => {
  try {
    let apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/efota/onPrem/requestFirmwareList`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ customerLicense })
    })
    const responseJSON = await response.json()
    return responseJSON
  } catch (err) {
    console.log(err)
    return Error(err)
  }
}

export const extendLicense = async (licenseKey, endDate, promotionId) => {
  try {
    let apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/license/extend`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ licenseKey, endDate, promotionId })
    })
    if (!response.ok) {
      return { requestID: response.headers.get('x-request-id') }
    }

    const responseJSON = await response.json()

    return responseJSON
  } catch (err) {
    console.log(err)
    return Error(err)
  }
}

export const getKpeStatus = async (incomingCustomerId) => {
  try {
    let apiUrl = `${publicRuntimeConfig.licenseApiBaseUrl}/api/v1/kpe/status/${
      incomingCustomerId || ''
    }`
    const response = await fetch(apiUrl)
    const responseJSON = await response.json()
    return responseJSON
  } catch (err) {
    console.log(err)
    return Error(err)
  }
}
